import { useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Switch,
  type SelectOption,
} from '../../components';
import { apiErrorMessage } from '../../lib/api';
import type { UserOption } from '../leads/types';
import type {
  EntityLookup,
  EventCreatePayload,
  EventRow,
  EventUpdatePayload,
} from './types';

type FormState = {
  title: string;
  description: string;
  event_type: string;
  is_all_day: boolean;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  user_id: string;
  lead_id: string;
  client_id: string;
  contact_id: string;
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function localDateParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function nowRoundedToHour(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(date: string, time: string): string | null {
  if (!date) return null;
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  const [hh, mm] = (time || '00:00').split(':').map((s) => parseInt(s, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0, 0);
  return dt.toISOString();
}

function allDayStartIso(date: string): string | null {
  if (!date) return null;
  return localToIso(date, '00:00');
}

function allDayEndIso(date: string): string | null {
  if (!date) return null;
  // End-of-day local time (23:59:59.999) so a same-day all-day event has a sensible duration
  // and end_at >= start_at validation passes on the backend.
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1, 23, 59, 59, 999);
  return dt.toISOString();
}

function toFormState(row?: EventRow, defaults?: { dateISO?: string }): FormState {
  if (row) {
    const start = localDateParts(row.start_at);
    const end = row.end_at ? localDateParts(row.end_at) : null;
    return {
      title: row.title,
      description: row.description ?? '',
      event_type: row.event_type ?? '',
      is_all_day: row.is_all_day,
      start_date: start.date,
      start_time: start.time,
      end_date: end?.date ?? '',
      end_time: end?.time ?? '',
      user_id: row.user_id ?? '',
      lead_id: row.lead_id ?? '',
      client_id: row.client_id ?? '',
      contact_id: row.contact_id ?? '',
    };
  }
  const date = defaults?.dateISO ?? todayLocal();
  return {
    title: '',
    description: '',
    event_type: '',
    is_all_day: false,
    start_date: date,
    start_time: nowRoundedToHour(),
    end_date: '',
    end_time: '',
    user_id: '',
    lead_id: '',
    client_id: '',
    contact_id: '',
  };
}

export function EventFormModal({
  row,
  defaultDateISO,
  canWrite = true,
  users,
  leads,
  clients,
  contacts,
  onClose,
  onSubmit,
  onDelete,
}: {
  row?: EventRow;
  defaultDateISO?: string;
  /** When false the modal is view-only: the Save button is hidden. */
  canWrite?: boolean;
  users: UserOption[];
  leads: EntityLookup[];
  clients: EntityLookup[];
  contacts: EntityLookup[];
  onClose: () => void;
  onSubmit: (payload: EventCreatePayload | EventUpdatePayload) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const [form, setForm] = useState<FormState>(() =>
    toFormState(row, { dateISO: defaultDateISO }),
  );
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userOpts: SelectOption[] = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.full_name })),
    [users],
  );
  const leadOpts: SelectOption[] = useMemo(
    () => leads.map((l) => ({ value: l.id, label: l.full_name ?? 'Unnamed lead' })),
    [leads],
  );
  const clientOpts: SelectOption[] = useMemo(
    () => clients.map((c) => ({ value: c.id, label: c.full_name ?? 'Unnamed client' })),
    [clients],
  );
  const contactOpts: SelectOption[] = useMemo(
    () => contacts.map((c) => ({ value: c.id, label: c.full_name ?? 'Unnamed contact' })),
    [contacts],
  );

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const title = form.title.trim();
    if (!title) {
      setError('Title is required');
      return;
    }
    if (!form.start_date) {
      setError('Start date is required');
      return;
    }

    const startIso = form.is_all_day
      ? allDayStartIso(form.start_date)
      : localToIso(form.start_date, form.start_time || '00:00');
    if (!startIso) {
      setError('Invalid start date/time');
      return;
    }

    let endIso: string | null = null;
    if (form.is_all_day) {
      const endDate = form.end_date || form.start_date;
      endIso = allDayEndIso(endDate);
    } else if (form.end_date) {
      endIso = localToIso(form.end_date, form.end_time || form.start_time || '00:00');
    } else if (form.end_time && form.end_time > form.start_time) {
      endIso = localToIso(form.start_date, form.end_time);
    }

    if (endIso && new Date(endIso).getTime() < new Date(startIso).getTime()) {
      setError('End must be the same as or after start');
      return;
    }

    const s = (v: string) => (v.trim() === '' ? null : v.trim());
    const payload: EventCreatePayload | EventUpdatePayload = {
      title,
      description: s(form.description),
      event_type: s(form.event_type),
      start_at: startIso,
      end_at: endIso,
      is_all_day: form.is_all_day,
      user_id: form.user_id || null,
      lead_id: form.lead_id || null,
      client_id: form.client_id || null,
      contact_id: form.contact_id || null,
    };

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!onDelete) return;
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    setError(null);
    setDeleting(true);
    try {
      await onDelete();
    } catch (err) {
      setError(apiErrorMessage(err, 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit event' : 'New event'} width={620}>
      <form onSubmit={handleSubmit}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            id="ev-title"
            label="Title"
            value={form.title}
            onChange={(v) => setField('title', v)}
            required
            autoFocus
          />

          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Input
                id="ev-type"
                label="Type"
                placeholder="meeting, call, demo…"
                value={form.event_type}
                onChange={(v) => setField('event_type', v)}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                paddingBottom: 9,
                paddingLeft: 4,
              }}
            >
              <Switch
                on={form.is_all_day}
                onChange={(v) => setField('is_all_day', v)}
                label="All day"
              />
              <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>All day</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="ev-start-date"
                label="Start date"
                type="date"
                value={form.start_date}
                onChange={(v) => setField('start_date', v)}
                required
              />
            </div>
            {!form.is_all_day && (
              <div style={{ flex: 1 }}>
                <Input
                  id="ev-start-time"
                  label="Start time"
                  type="time"
                  value={form.start_time}
                  onChange={(v) => setField('start_time', v)}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="ev-end-date"
                label={form.is_all_day ? 'End date (optional)' : 'End date (optional)'}
                type="date"
                value={form.end_date}
                onChange={(v) => setField('end_date', v)}
                placeholder={form.is_all_day ? 'same as start' : ''}
              />
            </div>
            {!form.is_all_day && (
              <div style={{ flex: 1 }}>
                <Input
                  id="ev-end-time"
                  label="End time (optional)"
                  type="time"
                  value={form.end_time}
                  onChange={(v) => setField('end_time', v)}
                />
              </div>
            )}
          </div>

          <Select
            id="ev-owner"
            label="Owner"
            placeholder="— current user —"
            value={form.user_id}
            options={userOpts}
            onChange={(v) => setField('user_id', v)}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <Select
              id="ev-lead"
              label="Linked lead"
              placeholder="— none —"
              value={form.lead_id}
              options={leadOpts}
              onChange={(v) => setField('lead_id', v)}
            />
            <Select
              id="ev-client"
              label="Linked client"
              placeholder="— none —"
              value={form.client_id}
              options={clientOpts}
              onChange={(v) => setField('client_id', v)}
            />
            <Select
              id="ev-contact"
              label="Linked contact"
              placeholder="— none —"
              value={form.contact_id}
              options={contactOpts}
              onChange={(v) => setField('contact_id', v)}
            />
          </div>

          <div>
            <label className="field-label" htmlFor="ev-desc">
              Description
            </label>
            <textarea
              id="ev-desc"
              className="input"
              rows={3}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </div>

          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{
            justifyContent: 'space-between',
            borderTop: '1px solid var(--border)',
            borderBottom: 'none',
          }}
        >
          <div>
            {row && onDelete && (
              <Button
                type="button"
                variant="danger"
                icon="trash"
                onClick={handleDelete}
                disabled={submitting || deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!canWrite && (
              <span className="muted" style={{ fontSize: 12 }}>
                View only
              </span>
            )}
            <Button type="button" onClick={onClose} disabled={submitting || deleting}>
              {canWrite ? 'Cancel' : 'Close'}
            </Button>
            {canWrite && (
              <Button variant="primary" type="submit" disabled={submitting || deleting}>
                {submitting ? 'Saving…' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}
